import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../sidebar/TopBar";
import { END_POINT } from "../../../helper/helper";
import { useNavigate } from "react-router-dom";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const emptyCategory = { value: "", label: "", count: 0 };
const emptyToolForm = {
  name: "", company: "", category: "", description: "",
  pricing: "Freemium", price: "", rating: 0, reviews: 0,
  icon: "", website: "", tagsCsv: "", featuresCsv: "",
  usecaseCsv: "", platformCsv: "", badgesCsv: "",
  youtube: "", tutorial: "", demo: "", releaseDate: "", newFeaturesCsv: "",
};

const csvToArray = (v) => v.split(",").map((s) => s.trim()).filter(Boolean);

const toolToForm = (tool) => ({
  name: tool.name || "", company: tool.company || "", category: tool.category || "",
  description: tool.description || "", pricing: tool.pricing || "Freemium",
  price: tool.price || "", rating: tool.rating || 0, reviews: tool.reviews || 0,
  icon: tool.icon || "", website: tool.website || "",
  tagsCsv: (tool.tags || []).join(", "),
  featuresCsv: (tool.features || []).join(", "),
  usecaseCsv: (tool.usecase || []).join(", "),
  platformCsv: (tool.platform || []).join(", "),
  badgesCsv: (tool.badges || []).join(", "),
  youtube: tool.videos?.youtube || "", tutorial: tool.videos?.tutorial || "",
  demo: tool.videos?.demo || "", releaseDate: tool.newFeatures?.releaseDate || "",
  newFeaturesCsv: (tool.newFeatures?.features || []).join(", "),
});


const formToTool = (form) => ({
  name: form.name.trim(), company: form.company.trim(), category: form.category.trim(),
  description: form.description.trim(), pricing: form.pricing,
  price: form.price.trim(), rating: Number(form.rating) || 0,
  reviews: Number(form.reviews) || 0, icon: form.icon.trim(),
  tags: csvToArray(form.tagsCsv), features: csvToArray(form.featuresCsv),
  usecase: csvToArray(form.usecaseCsv), platform: csvToArray(form.platformCsv),
  badges: csvToArray(form.badgesCsv), website: form.website.trim(),
  videos: { youtube: form.youtube.trim(), tutorial: form.tutorial.trim(), demo: form.demo.trim() },
  newFeatures: form.releaseDate.trim() || form.newFeaturesCsv.trim()
    ? { releaseDate: form.releaseDate.trim(), features: csvToArray(form.newFeaturesCsv) }
    : null,
});



// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const c = {
  bg: "#F7F8FC",
  surface: "#FFFFFF",
  border: "#E8ECF4",
  borderHover: "#CBD5E0",
  text: "#1A202C",
  textMuted: "#718096",
  textFaint: "#A0AEC0",
  accent: "#4F46E5",
  accentLight: "#EEF2FF",
  accentBorder: "#C7D2FE",
  success: "#059669",
  successLight: "#D1FAE5",
  danger: "#DC2626",
  dangerLight: "#FEE2E2",
  warning: "#D97706",
  warningLight: "#FEF3C7",
};

// ─── SHARED STYLES ────────────────────────────────────────────────────────────
const inputBase = {
  width: "100%", padding: "9px 12px", fontSize: "13px",
  border: `1px solid ${c.border}`, borderRadius: "8px",
  background: c.bg, color: c.text, outline: "none",
  fontFamily: "'Inter', system-ui, sans-serif",
  transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block", fontSize: "11px", fontWeight: 600,
  color: c.textMuted, marginBottom: "5px", letterSpacing: "0.05em", textTransform: "uppercase",
};

const sectionTitle = {
  fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em",
  textTransform: "uppercase", color: c.textFaint, marginBottom: "12px",
};

// ─── FIELD ────────────────────────────────────────────────────────────────────
const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: "14px" }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: c.accent }}> *</span>}</label>
    {children}
  </div>
);

const Input = ({ ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? c.accent : c.border,
        boxShadow: focused ? `0 0 0 3px ${c.accentLight}` : "none",
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Textarea = ({ ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      {...props}
      style={{
        ...inputBase,
        resize: "vertical",
        borderColor: focused ? c.accent : c.border,
        boxShadow: focused ? `0 0 0 3px ${c.accentLight}` : "none",
        lineHeight: "1.5",
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const Select = ({ children, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...inputBase,
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23718096' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 12px center",
        paddingRight: "32px",
        cursor: "pointer",
        borderColor: focused ? c.accent : c.border,
        boxShadow: focused ? `0 0 0 3px ${c.accentLight}` : "none",
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {children}
    </select>
  );
};

// ─── BUTTONS ─────────────────────────────────────────────────────────────────
const Btn = ({ variant = "primary", size = "md", children, ...props }) => {
  const [hovered, setHovered] = useState(false);
  const base = {
    border: "none", borderRadius: "8px", cursor: props.disabled ? "not-allowed" : "pointer",
    fontWeight: 600, fontFamily: "'Inter', system-ui, sans-serif",
    transition: "all 0.15s", display: "inline-flex", alignItems: "center", gap: "6px",
  };
  const sizes = { sm: { padding: "6px 10px", fontSize: "12px" }, md: { padding: "9px 16px", fontSize: "13px" } };
  const variants = {
    primary: { background: hovered && !props.disabled ? "#4338CA" : c.accent, color: "#fff", opacity: props.disabled ? 0.6 : 1 },
    ghost: { background: hovered ? c.border : "transparent", color: c.textMuted, border: `1px solid ${c.border}` },
    danger: { background: hovered ? "#B91C1C" : c.dangerLight, color: c.danger, border: `1px solid #FECACA` },
    edit: { background: hovered ? c.accentLight : "#F1F5FF", color: c.accent, border: `1px solid ${c.accentBorder}` },
  };
  return (
    <button
      {...props}
      style={{ ...base, ...sizes[size], ...variants[variant] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </button>
  );
};

// ─── BADGE ────────────────────────────────────────────────────────────────────
const Badge = ({ children, color = c.accent }) => (
  <span style={{
    display: "inline-block", padding: "2px 8px", borderRadius: "20px",
    fontSize: "10px", fontWeight: 600, letterSpacing: "0.04em",
    background: color + "18", color,
    border: `1px solid ${color}30`,
  }}>{children}</span>
);

const pricingColor = (p) =>
  p === "Free" ? c.success : p === "Freemium" ? c.warning : c.accent;

// ─── DIVIDER ─────────────────────────────────────────────────────────────────
const SectionDivider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "20px 0 16px" }}>
    <div style={{ height: "1px", flex: 1, background: c.border }} />
    <span style={{ fontSize: "10px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{label}</span>
    <div style={{ height: "1px", flex: 1, background: c.border }} />
  </div>
);

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const HomeDirectoryManager = () => {

const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [activeTab, setActiveTab] = useState("categories");
  const [categories, setCategories] = useState([]);
  const [tools, setTools] = useState([]);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [toolForm, setToolForm] = useState(emptyToolForm);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);
  const [editingToolIndex, setEditingToolIndex] = useState(null);
  const [toolSearch, setToolSearch] = useState("");
  const categoryValues = useMemo(() => categories.map((c) => c.value), [categories]);

  const setMsg = (text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(END_POINT.GetHomeDirectoryData);
      const result = await res.json();
      if (result?.success && result?.data) {
        setCategories(Array.isArray(result.data.categories) ? result.data.categories : []);
        setTools(Array.isArray(result.data.aiTools) ? result.data.aiTools : []);
      }
    } catch { setMsg("Failed to load data.", "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const persistData = async (nextCategories, nextTools) => {
    setSaving(true);
    try {
      const res = await fetch(END_POINT.UpsertHomeDirectoryData, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categories: nextCategories, aiTools: nextTools }),
      });
      const result = await res.json();
      if (!result?.success) throw new Error();
      setCategories(nextCategories);
      setTools(nextTools);
      setMsg("Changes saved successfully.", "success");
      return true;
    } catch { setMsg("Save failed. Please try again.", "error"); return false; }
    finally { setSaving(false); }
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.value.trim() || !categoryForm.label.trim()) {
      setMsg("Category value and label are required.", "error"); return;
    }
    const payload = { value: categoryForm.value.trim(), label: categoryForm.label.trim(), count: Number(categoryForm.count) || 0 };
    const next = [...categories];
    if (editingCategoryIndex === null) next.push(payload);
    else next[editingCategoryIndex] = payload;
    const ok = await persistData(next, tools);
    if (ok) { setCategoryForm(emptyCategory); setEditingCategoryIndex(null); }
  };

  const editCategory = (i) => { setCategoryForm({ ...categories[i] }); setEditingCategoryIndex(i); };
  const deleteCategory = async (i) => {
    const next = categories.filter((_, idx) => idx !== i);
    const ok = await persistData(next, tools);
    if (ok && editingCategoryIndex === i) { setCategoryForm(emptyCategory); setEditingCategoryIndex(null); }
  };

  const submitTool = async (e) => {
    e.preventDefault();
    if (!toolForm.name.trim() || !toolForm.category.trim()) {
      setMsg("Tool name and category are required.", "error"); return;
    }
    const payload = formToTool(toolForm);
    const next = [...tools];
    if (editingToolIndex === null) next.push(payload);
    else next[editingToolIndex] = payload;
    const ok = await persistData(categories, next);
    if (ok) { setToolForm(emptyToolForm); setEditingToolIndex(null); }
  };

  const editTool = (i) => { setToolForm(toolToForm(tools[i])); setEditingToolIndex(i); };
  const deleteTool = async (i) => {
    const next = tools.filter((_, idx) => idx !== i);
    const ok = await persistData(categories, next);
    if (ok && editingToolIndex === i) { setToolForm(emptyToolForm); setEditingToolIndex(null); }
  };

  const filteredTools = tools.filter(t =>
    t.name?.toLowerCase().includes(toolSearch.toLowerCase()) ||
    t.category?.toLowerCase().includes(toolSearch.toLowerCase()) ||
    t.company?.toLowerCase().includes(toolSearch.toLowerCase())
  );

  const msgColors = {
    success: { bg: c.successLight, color: c.success, border: "#A7F3D0" },
    error: { bg: c.dangerLight, color: c.danger, border: "#FECACA" },
    info: { bg: c.accentLight, color: c.accent, border: c.accentBorder },
  };

  return (
    <div style={{ background: c.bg, minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        input, textarea, select { font-family: 'Inter', system-ui, sans-serif !important; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #CBD5E0; border-radius: 10px; }
      `}</style>

      <TopBar />

      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "28px 24px" }}>

        {/* ── Page Header ── */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "24px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
              <div style={{
                width: 36, height: 36, borderRadius: "10px",
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "16px",
              }}>🗂️</div>
              <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: c.text }}>AI Directory Manager</h1>
            </div>
            <p style={{ margin: 0, fontSize: "13px", color: c.textMuted }}>
              Manage categories and AI tools displayed on the HomeScreen directory.
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{ fontSize: "12px", color: c.textMuted, background: c.surface, border: `1px solid ${c.border}`, borderRadius: "8px", padding: "6px 12px" }}>
              <span style={{ fontWeight: 600, color: c.text }}>{categories.length}</span> categories &nbsp;·&nbsp; <span style={{ fontWeight: 600, color: c.text }}>{tools.length}</span> tools
            </div>
          </div>
        </div>

        {/* ── Toast ── */}
        {message.text && (
          <div style={{
            marginBottom: "16px", padding: "11px 16px", borderRadius: "10px",
            fontSize: "13px", fontWeight: 500, display: "flex", alignItems: "center", gap: "8px",
            background: (msgColors[message.type] || msgColors.info).bg,
            color: (msgColors[message.type] || msgColors.info).color,
            border: `1px solid ${(msgColors[message.type] || msgColors.info).border}`,
          }}>
            {message.type === "success" ? "✓" : message.type === "error" ? "✕" : "ℹ"} {message.text}
          </div>
        )}

        {/* ── Tabs ── */}
       <div style={{
  display: "flex", gap: "4px", marginBottom: "20px",
  background: c.surface, border: `1px solid ${c.border}`,
  borderRadius: "12px", padding: "4px", width: "fit-content",
}}>
  {[
    { key: "categories", label: "🏷️  Categories", count: categories.length },
    { key: "tools", label: "🤖  AI Tools", count: tools.length, href: "/admin/ai-tool" },
  ].map(tab => (
    <button
      key={tab.key}
      onClick={() => {
        if (tab.href) {
          window.location.href = tab.href; // or: navigate(tab.href)
        } else {
          setActiveTab(tab.key);
        }
      }}
      style={{
        border: "none", cursor: "pointer", borderRadius: "9px",
        padding: "8px 18px", fontSize: "13px", fontWeight: 600,
        fontFamily: "'Inter', system-ui, sans-serif",
        background: activeTab === tab.key ? c.accent : "transparent",
        color: activeTab === tab.key ? "#fff" : c.textMuted,
        transition: "all 0.15s",
        display: "flex", alignItems: "center", gap: "6px",
      }}
    >
      {tab.label}
      <span style={{
        fontSize: "10px", padding: "1px 6px", borderRadius: "10px", fontWeight: 700,
        background: activeTab === tab.key ? "rgba(255,255,255,0.25)" : c.border,
        color: activeTab === tab.key ? "#fff" : c.textMuted,
      }}>{tab.count}</span>
    </button>
  ))}
</div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "300px", gap: "12px", color: c.textMuted }}>
            <div style={{ width: 20, height: 20, border: `2px solid ${c.border}`, borderTopColor: c.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            Loading directory data…
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            {/* ══════════════ CATEGORIES TAB ══════════════ */}
            {activeTab === "categories" && (
              <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: "16px", alignItems: "start" }}>

                {/* Form */}
                <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${c.border}`, background: editingCategoryIndex !== null ? c.accentLight : c.bg }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: c.text }}>
                      {editingCategoryIndex !== null ? "✏️  Edit Category" : "➕  New Category"}
                    </div>
                    {editingCategoryIndex !== null && (
                      <div style={{ fontSize: "11px", color: c.accent, marginTop: "2px" }}>Editing: {categories[editingCategoryIndex]?.label}</div>
                    )}
                  </div>
                  <form onSubmit={submitCategory} style={{ padding: "20px" }}>
                    <Field label="Value" required>
                      <Input
                        placeholder="e.g. Chat"
                        value={categoryForm.value}
                        onChange={(e) => setCategoryForm({ ...categoryForm, value: e.target.value })}
                      />
                    </Field>
                    <Field label="Label" required>
                      <Input
                        placeholder="e.g. Chatbots & Assistants"
                        value={categoryForm.label}
                        onChange={(e) => setCategoryForm({ ...categoryForm, label: e.target.value })}
                      />
                    </Field>
                    <Field label="Tool Count">
                      <Input
                        type="number"
                        placeholder="0"
                        value={categoryForm.count}
                        onChange={(e) => setCategoryForm({ ...categoryForm, count: e.target.value })}
                      />
                    </Field>
                    <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                      <Btn type="submit" disabled={saving}>
                        {saving ? "Saving…" : editingCategoryIndex === null ? "Create Category" : "Save Changes"}
                      </Btn>
                      {editingCategoryIndex !== null && (
                        <Btn type="button" variant="ghost" onClick={() => { setEditingCategoryIndex(null); setCategoryForm(emptyCategory); }}>
                          Cancel
                        </Btn>
                      )}
                    </div>
                  </form>
                </div>

                {/* List */}
                <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: c.text }}>All Categories</div>
                    <Badge color={c.accent}>{categories.length} total</Badge>
                  </div>
                  <div style={{ padding: "12px" }}>
                    {categories.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "48px 24px", color: c.textFaint, fontSize: "13px" }}>
                        No categories yet. Create one to get started.
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: "6px" }}>
                        {categories.map((item, index) => (
                          <div
                            key={`${item.value}-${index}`}
                            style={{
                              border: `1px solid ${editingCategoryIndex === index ? c.accentBorder : c.border}`,
                              borderRadius: "10px", padding: "12px 14px",
                              display: "flex", justifyContent: "space-between", alignItems: "center",
                              background: editingCategoryIndex === index ? c.accentLight : "#FAFAFA",
                              transition: "all 0.15s",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <div style={{
                                width: 36, height: 36, borderRadius: "8px", flexShrink: 0,
                                background: `linear-gradient(135deg, ${c.accent}20, ${c.accent}40)`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "14px", fontWeight: 700, color: c.accent,
                              }}>{item.label?.[0] || "?"}</div>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: "13px", color: c.text }}>{item.label}</div>
                                <div style={{ fontSize: "11px", color: c.textMuted, marginTop: "2px" }}>
                                  value: <code style={{ background: c.border, borderRadius: "4px", padding: "1px 5px", fontSize: "10px" }}>{item.value}</code>
                                  &nbsp;·&nbsp; {item.count || 0} tools
                                </div>
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: "6px" }}>
                              <Btn size="sm" variant="edit" type="button" onClick={() => editCategory(index)}>Edit</Btn>
                              <Btn size="sm" variant="danger" type="button" onClick={() => deleteCategory(index)}>Delete</Btn>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ══════════════ TOOLS TAB ══════════════ */}
            {activeTab === "tools" && (
              <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: "16px", alignItems: "start" }}>

                {/* Form */}
                <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", position: "sticky", top: "16px" }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${c.border}`, background: editingToolIndex !== null ? c.accentLight : c.bg }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: c.text }}>
                      {editingToolIndex !== null ? "✏️  Edit Tool" : "➕  New AI Tool"}
                    </div>
                    {editingToolIndex !== null && (
                      <div style={{ fontSize: "11px", color: c.accent, marginTop: "2px" }}>Editing: {tools[editingToolIndex]?.name}</div>
                    )}
                  </div>
                  <form onSubmit={submitTool} style={{ padding: "20px", maxHeight: "calc(100vh - 280px)", overflowY: "auto" }}>

                    <div style={sectionTitle}>Basic Info</div>
                    <Field label="Tool Name" required>
                      <Input placeholder="e.g. ChatGPT" value={toolForm.name} onChange={(e) => setToolForm({ ...toolForm, name: e.target.value })} />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      <Field label="Company">
                        <Input placeholder="e.g. OpenAI" value={toolForm.company} onChange={(e) => setToolForm({ ...toolForm, company: e.target.value })} />
                      </Field>
                      <Field label="Icon">
                        <Input placeholder="Emoji or text" value={toolForm.icon} onChange={(e) => setToolForm({ ...toolForm, icon: e.target.value })} />
                      </Field>
                    </div>
                    <Field label="Category" required>
                      <Input placeholder="Must match category value" list="category-values" value={toolForm.category} onChange={(e) => setToolForm({ ...toolForm, category: e.target.value })} />
                      <datalist id="category-values">{categoryValues.map((v) => <option key={v} value={v} />)}</datalist>
                    </Field>
                    <Field label="Description">
                      <Textarea placeholder="Short description of the tool…" value={toolForm.description} onChange={(e) => setToolForm({ ...toolForm, description: e.target.value })} rows={3} />
                    </Field>

                    <SectionDivider label="Pricing" />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      <Field label="Pricing Model">
                        <Select value={toolForm.pricing} onChange={(e) => setToolForm({ ...toolForm, pricing: e.target.value })}>
                          <option>Free</option>
                          <option>Freemium</option>
                          <option>Paid</option>
                        </Select>
                      </Field>
                      <Field label="Price Text">
                        <Input placeholder="e.g. From $20/mo" value={toolForm.price} onChange={(e) => setToolForm({ ...toolForm, price: e.target.value })} />
                      </Field>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      <Field label="Rating">
                        <Input type="number" step="0.1" min="0" max="5" placeholder="0.0" value={toolForm.rating} onChange={(e) => setToolForm({ ...toolForm, rating: e.target.value })} />
                      </Field>
                      <Field label="Reviews">
                        <Input type="number" placeholder="0" value={toolForm.reviews} onChange={(e) => setToolForm({ ...toolForm, reviews: e.target.value })} />
                      </Field>
                    </div>

                    <SectionDivider label="Metadata (comma-separated)" />
                    <Field label="Tags">
                      <Input placeholder="AI, NLP, Writing…" value={toolForm.tagsCsv} onChange={(e) => setToolForm({ ...toolForm, tagsCsv: e.target.value })} />
                    </Field>
                    <Field label="Features">
                      <Input placeholder="Chat, API, Plugins…" value={toolForm.featuresCsv} onChange={(e) => setToolForm({ ...toolForm, featuresCsv: e.target.value })} />
                    </Field>
                    <Field label="Use Cases">
                      <Input placeholder="Customer support, Coding…" value={toolForm.usecaseCsv} onChange={(e) => setToolForm({ ...toolForm, usecaseCsv: e.target.value })} />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      <Field label="Platforms">
                        <Input placeholder="Web, iOS, Android…" value={toolForm.platformCsv} onChange={(e) => setToolForm({ ...toolForm, platformCsv: e.target.value })} />
                      </Field>
                      <Field label="Badges">
                        <Input placeholder="New, Hot, Staff Pick…" value={toolForm.badgesCsv} onChange={(e) => setToolForm({ ...toolForm, badgesCsv: e.target.value })} />
                      </Field>
                    </div>

                    <SectionDivider label="Links & Media" />
                    <Field label="Website URL">
                      <Input placeholder="https://…" value={toolForm.website} onChange={(e) => setToolForm({ ...toolForm, website: e.target.value })} />
                    </Field>
                    <Field label="YouTube Embed URL">
                      <Input placeholder="https://youtube.com/embed/…" value={toolForm.youtube} onChange={(e) => setToolForm({ ...toolForm, youtube: e.target.value })} />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      <Field label="Tutorial URL">
                        <Input placeholder="https://…" value={toolForm.tutorial} onChange={(e) => setToolForm({ ...toolForm, tutorial: e.target.value })} />
                      </Field>
                      <Field label="Demo / Docs URL">
                        <Input placeholder="https://…" value={toolForm.demo} onChange={(e) => setToolForm({ ...toolForm, demo: e.target.value })} />
                      </Field>
                    </div>

                    <SectionDivider label="New Features" />
                    <Field label="Release Date">
                      <Input placeholder="e.g. 2024-01-15" value={toolForm.releaseDate} onChange={(e) => setToolForm({ ...toolForm, releaseDate: e.target.value })} />
                    </Field>
                    <Field label="New Features">
                      <Input placeholder="Feature 1, Feature 2…" value={toolForm.newFeaturesCsv} onChange={(e) => setToolForm({ ...toolForm, newFeaturesCsv: e.target.value })} />
                    </Field>

                    <div style={{ display: "flex", gap: "8px", paddingTop: "4px" }}>
                      <Btn type="submit" disabled={saving}>
                        {saving ? "Saving…" : editingToolIndex === null ? "Create Tool" : "Save Changes"}
                      </Btn>
                      {editingToolIndex !== null && (
                        <Btn type="button" variant="ghost" onClick={() => { setEditingToolIndex(null); setToolForm(emptyToolForm); }}>
                          Cancel
                        </Btn>
                      )}
                    </div>
                  </form>
                </div>

                {/* Tools List */}
                <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: c.text, flexShrink: 0 }}>All Tools</div>
                    <div style={{ flex: 1, position: "relative" }}>
                      <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "13px", color: c.textFaint }}>🔍</span>
                      <input
                        placeholder="Search by name, category, company…"
                        value={toolSearch}
                        onChange={e => setToolSearch(e.target.value)}
                        style={{ ...inputBase, paddingLeft: "30px", fontSize: "12px" }}
                      />
                    </div>
                    <Badge color={c.accent}>{filteredTools.length} / {tools.length}</Badge>
                  </div>
                  <div style={{ padding: "12px", maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}>
                    {filteredTools.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "48px 24px", color: c.textFaint, fontSize: "13px" }}>
                        {tools.length === 0 ? "No tools yet. Add your first AI tool." : "No tools match your search."}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: "6px" }}>
                        {filteredTools.map((tool, index) => {
                          const realIndex = tools.indexOf(tool);
                          return (
                            <div
                              key={`${tool.name}-${realIndex}`}
                              style={{
                                border: `1px solid ${editingToolIndex === realIndex ? c.accentBorder : c.border}`,
                                borderRadius: "10px", padding: "12px 14px",
                                display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px",
                                background: editingToolIndex === realIndex ? c.accentLight : "#FAFAFA",
                                transition: "all 0.15s",
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                                <div style={{
                                  width: 38, height: 38, borderRadius: "10px", flexShrink: 0,
                                  background: `linear-gradient(135deg, #f0f4ff, #e0e7ff)`,
                                  border: `1px solid ${c.border}`,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: "18px",
                                }}>{tool.icon || "🤖"}</div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, fontSize: "13px", color: c.text, display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                    {tool.name}
                                    {tool.pricing && <Badge color={pricingColor(tool.pricing)}>{tool.pricing}</Badge>}
                                    {tool.rating > 0 && <span style={{ fontSize: "11px", color: c.warning }}>★ {Number(tool.rating).toFixed(1)}</span>}
                                  </div>
                                  <div style={{ fontSize: "11px", color: c.textMuted, marginTop: "3px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                    {tool.company && <span>{tool.company}</span>}
                                    {tool.category && <><span>·</span><code style={{ background: c.border, borderRadius: "4px", padding: "1px 5px", fontSize: "10px" }}>{tool.category}</code></>}
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                                <Btn size="sm" variant="edit" type="button" onClick={() => editTool(realIndex)}>Edit</Btn>
                                <Btn size="sm" variant="danger" type="button" onClick={() => deleteTool(realIndex)}>Delete</Btn>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HomeDirectoryManager;